import { InfiniteQueryObserverBaseResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
  fetchNextPage?: InfiniteQueryObserverBaseResult["fetchNextPage"];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  observerKey?: string | number | boolean;
}

const useInfiniteScroll = ({
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  observerKey,
}: UseInfiniteScrollOptions) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (loadingRef.current && hasNextPage && fetchNextPage) {
      observerRef.current = new IntersectionObserver(
        entries => {
          const [entry] = entries;

          if (entry.isIntersecting && !isFetchingNextPage) {
            void fetchNextPage();
          }
        },
        {
          root: tabListRef.current,
          rootMargin: "200px",
          threshold: 0.1,
        },
      );

      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, observerKey]);

  return { isLoadingMore: isFetchingNextPage, loadingRef, tabListRef };
};
export default useInfiniteScroll;
